"use client";

import type {
  ColumnDef,
  VisibilityState,
  SortingState,
  Table as TableType,
  ColumnFiltersState,
} from "@tanstack/react-table";

import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  getSortedRowModel,
  getFilteredRowModel,
} from "@tanstack/react-table";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./table";

import { useEffect, useMemo, useState } from "react";
import { DataTablePagination } from "./data-table-pagination";
import { DataTableViewOptions } from "./data-table-view-options";
import { Checkbox } from "./checkbox";

interface DataTableProps<TData, TValue> {
  className?: string;
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  filters?: (
    table: TableType<TData>,
    selectedRows: TData[],
    clearRowSelection: () => void
  ) => React.ReactNode;
  mainActions?: () => React.ReactNode;
  isSelectable?: boolean;
  onFilteredDataChange?: (data: TData[]) => void;
  onRowClick?: (row: TData) => void;
  total: number;
}

export function DataTable<TData, TValue>({
  className,
  columns,
  data,
  filters,
  mainActions,
  isSelectable = false,
  onFilteredDataChange,
  onRowClick,
  total,
}: DataTableProps<TData, TValue>) {
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [globalFilter, setGlobalFilter] = useState<string>("");
  const [rowSelection, setRowSelection] = useState({});

  const finalColumns = useMemo(() => {
    const userColumns = columns.map((column) => ({
      ...column,
      enableSorting: false,
    }));
    if (isSelectable) {
      return [
        {
          id: "select",
          header: ({ table }) => (
            <Checkbox
              checked={
                table.getIsAllPageRowsSelected() ||
                (table.getIsSomePageRowsSelected() && "indeterminate")
              }
              onCheckedChange={(value) =>
                table.toggleAllPageRowsSelected(!!value)
              }
              aria-label="Seleccionar todos"
            />
          ),
          cell: ({ row }) => (
            <Checkbox
              onClick={(e) => e.stopPropagation()}
              checked={row.getIsSelected()}
              onCheckedChange={(value) => row.toggleSelected(!!value)}
              aria-label="Seleccionar fila"
            />
          ),
          enableSorting: false,
          enableHiding: false,
        },
        ...userColumns,
      ];
    }
    return userColumns;
  }, [columns, isSelectable]);

  const table = useReactTable({
    data,
    columns: finalColumns,
    getCoreRowModel: getCoreRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    onColumnFiltersChange: setColumnFilters,
    getFilteredRowModel: getFilteredRowModel(),
    onRowSelectionChange: setRowSelection,
    state: {
      globalFilter,
      columnVisibility,
      sorting,
      columnFilters,
      rowSelection,
    },
    onGlobalFilterChange: setGlobalFilter,
  });

  useEffect(() => {
    onFilteredDataChange?.(
      table.getFilteredRowModel().rows.map((row) => row.original)
    );
  }, [table, columnFilters, globalFilter, data, onFilteredDataChange]);

  return (
    <div className={className}>
      <div className="flex items-center pb-4 gap-2">
        {mainActions && mainActions()}
        {filters &&
          filters(
            table,
            table.getSelectedRowModel().rows.map((row) => row.original),
            () => setRowSelection({})
          )}
        <DataTableViewOptions table={table} />
      </div>
      <div className="overflow-hidden rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header, index) => {
                  if (
                    (
                      header.column.columnDef as unknown as {
                        hidden: boolean;
                      }
                    ).hidden
                  ) {
                    return null;
                  }
                  let className = "";
                  const columnDef = header.column.columnDef as unknown as {
                    accessorKey: string;
                  };
                  if (columnDef.accessorKey === "actions") {
                    className = "width-[1%] whitespace-nowrap text-right pr-4";
                  }
                  if (isSelectable && index === 0) {
                    className = "whitespace-nowrap w-[40px]";
                  }
                  return (
                    <TableHead key={header.id} className={className}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => {
                return (
                  <TableRow
                    key={row.id}
                    data-state={row.getIsSelected() && "selected"}
                    onClick={() => onRowClick?.(row.original)}
                    className={
                      onRowClick
                        ? "cursor-pointer hover:bg-muted/50"
                        : undefined
                    }
                  >
                    {row
                      .getVisibleCells()
                      .filter(
                        (cell) =>
                          !(
                            cell.column.columnDef as unknown as {
                              hidden: boolean;
                            }
                          ).hidden
                      )
                      .map((cell, index) => {
                        let className = "";
                        if (index + 1 === cell.row.getVisibleCells().length) {
                          className = "text-right w-[1%]";
                        }
                        if (isSelectable && index === 0) {
                          className = "";
                        }
                        return (
                          <TableCell key={cell.id} className={className}>
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </TableCell>
                        );
                      })}
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell
                  colSpan={finalColumns.length}
                  className="h-24 text-center"
                >
                  No hay resultados.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      {total > 0 && total > data.length && (
        <div className="pt-4">
          <DataTablePagination
            table={table}
            isSelectable={isSelectable}
            total={total}
          />
        </div>
      )}
    </div>
  );
}
